import * as React from 'react'
import * as validator from 'validator'
import { Row, Grid, Col, Image, Navbar } from 'react-bootstrap'
import SignupForm from './SignupForm'
import SignupFormNew from './SignupFormNew'
import Flash from './../common/flash'
import { TrackEvent } from './../common/Analytics'
import { match } from 'react-router-dom'
import { captureExceptionWithSentry } from '../sentry'
import './SignupForm.scss'

interface SignupContainerProps {
	language?: string
	embed?: boolean
	match: match<Record<string, string | undefined>>
}

export interface AccountData {
	username: string
	org: string
	firstName: string
	lastName: string
	password: string
	language: string
	currency: string
	referralCode: string
	signupUsingMedia: boolean
}

interface SignupContainerState {
	accountData: AccountData
	creating: boolean
	disableSendBtn: boolean
	flash?: string
	message: Record<string, unknown>
}

declare global {
	interface Window {
		serviceName?: string
		logoUrl?: string
	}
}

class Signup extends React.Component<SignupContainerProps, SignupContainerState> {
	private serviceName = window.serviceName ?? 'Readpeak'
	private logoUrl = window.logoUrl || '/img/readpeak-slogan.png'

	private signUpSuccess = false
	private eventCategoryPrefix = this.props.embed ? '(embed) ' : ''

	constructor(props: SignupContainerProps) {
		super(props)

		this.state = {
			creating: false,
			disableSendBtn: false,
			message: {},
			accountData: {
				signupUsingMedia: false,
				username: '',
				org: '',
				firstName: '',
				lastName: '',
				password: '',
				language: props.language === 'fi' ? 'fi' : '',
				currency: 'EUR',
				referralCode: props.match.params.token ?? '',
			},
		}
	}

	// Track form abandonment with fields which are set
	public triggerFormAbandonmentEvent = () => {
		if (this.signUpSuccess) {
			return
		}
		const category = this.eventCategoryPrefix + 'sign up form abandonment'
		const action = []
		const data = this.state.accountData

		if (data.username) {
			action.push('username')
		}
		if (data.org) {
			action.push('company name')
		}
		if (data.firstName) {
			action.push('first name')
		}
		if (data.lastName) {
			action.push('last name')
		}
		if (data.password) {
			action.push('password')
		}
		if (data.language) {
			action.push('language')
		}
		if (data.currency) {
			action.push('currency')
		}

		const element = document && document.querySelector('.alert.alert-danger')
		const label = (element && element.textContent) || undefined

		TrackEvent(category, action.length > 0 ? action.join(' > ') : '(nothing set)', label, 0, true)
	}

	public componentDidMount() {
		window.addEventListener('beforeunload', this.triggerFormAbandonmentEvent)
	}

	public UNSAFE_componentWillUnmount() {
		window.removeEventListener('beforeunload', this.triggerFormAbandonmentEvent)
	}

	public validate(obj: AccountData) {
		if (!validator.isEmail(obj.username)) {
			this.setState({ flash: 'username' })
			return false
		}

		if (!obj.password && !this.state.accountData.signupUsingMedia) {
			this.setState({ flash: 'password' })
			return false
		}

		if (!obj.firstName) {
			this.setState({ flash: 'firstname' })
			return false
		}

		if (!obj.org) {
			this.setState({ flash: 'companyName' })
			return false
		}

		if (!obj.language) {
			this.setState({ flash: 'language' })
			return false
		}

		return true
	}

	private setAccountData = <K extends keyof AccountData>(key: K, val: AccountData[K]) => {
		const accountData: AccountData = { ...this.state.accountData }
		accountData[key] = val
		this.setState({ flash: undefined, accountData })
	}

	private createAccount = async () => {
		if (this.validate(this.state.accountData)) {
			this.setState({ flash: undefined, creating: true })
			try {
				const resp = await fetch('/users/checkSignupEmail', {
					credentials: 'same-origin',
					method: 'POST',
					body: JSON.stringify({ email: this.state.accountData.username }),
				})
				const res = await resp.text()
				if (res === 'DUPLICATE') {
					const message = {
						title:
							this.props.language === 'fi'
								? 'Sähköpostiosoite on jo käytössä!'
								: 'This email has already been taken!',
						text:
							this.props.language === 'fi'
								? 'Valitse toinen sähköpostiosoite.'
								: 'Please correct the error and retry.',
						style: 'danger',
					}

					this.setState({
						creating: false,
						message,
						flash: 'misc',
					})
					TrackEvent(this.eventCategoryPrefix + 'sign up', 'fail', message.title + ' ' + message.text)
				} else {
					const respo = await fetch('/groups/createAdvertiserAccount', {
						credentials: 'same-origin',
						method: 'POST',
						body: JSON.stringify({
							accountData: JSON.stringify(this.state.accountData),
							language: this.props.language,
						}),
					})

					const result = await respo.json()
					if (result.hasOwnProperty('errors')) {
						const message = {
							title:
								this.props.language === 'fi'
									? 'Rekisteröityminen epäonnistui!'
									: 'There was a problem creating your account!',
							text: Object.keys(result.errors)
								.map((key) => result.errors[key])
								.join(' '),
							style: 'danger',
						}
						this.setState({
							creating: false,
							message,
							flash: 'misc',
						})
						TrackEvent(this.eventCategoryPrefix + 'sign up', 'fail', message.title + ' ' + message.text)
					} else {
						if (result.status === 'in' || result.status === 'auth error') {
							const url = result.status === 'in' ? '/users/signupLogin' : '/login'
							this.setState({ creating: false, flash: 'success' })
							setTimeout(() => {
								if (this.props.embed) {
									// this works if the action is not blocked by the iframes sandbox attribute
									window.top!.location.href = url
								} else {
									window.location.replace(url)
								}
							}, 3000)
							this.signUpSuccess = true
							TrackEvent(this.eventCategoryPrefix + 'sign up', 'success')
						}
					}
				}
			} catch (error) {
				captureExceptionWithSentry(error)
				const message = {
					title:
						this.props.language === 'fi'
							? 'Rekisteröityminen epäonnistui!'
							: 'There was a problem creating your account!',
					// text: error.message,
					style: 'danger',
				}
				this.setState({
					creating: false,
					message,
					flash: 'misc',
				})
				TrackEvent(this.eventCategoryPrefix + 'sign up', 'fail', message.title + ' ' + error.message)
			}
		} else {
			TrackEvent(this.eventCategoryPrefix + 'sign up', 'fail', this.state.flash)
		}
	}

	public render() {
		const displayFlash = () => {
			if (this.state.flash === 'misc') {
				return Flash(this.state.message)
			}
			return Flash({ feature: 'groupSignup', message: `${this.state.flash}_${this.props.language || 'en'}` })
		}

		const headerStyle: React.CSSProperties = {
			color: '#FF6666',
			textAlign: 'center',
		}
		const stringStyle: React.CSSProperties = {
			textAlign: 'center',
		}
		const imgStyle: React.CSSProperties = {
			display: 'block',
			marginLeft: 'auto',
			marginRight: 'auto',
		}

		return (
			<div className={this.props.embed ? 'shed-form__wrapper' : ''}>
				{!this.props.embed && (
					<Navbar className="navbar-custom">
						<Navbar.Header>
							<Navbar.Brand>
								<Image className="img-responsive" src={this.logoUrl} />
							</Navbar.Brand>
						</Navbar.Header>
					</Navbar>
				)}

				<Grid>
					{!this.props.embed && (
						<>
							<Row>
								<Col mdOffset={3} md={6}>
									<Image style={imgStyle} className="img-responsive" src="/img/signup-lead.png" />
								</Col>
							</Row>
							<Row>
								<Col mdOffset={3} md={6}>
									<h2 style={headerStyle}>
										{this.props.language === 'fi'
											? `Luo ${this.serviceName}-tunnus`
											: `Create Your ${this.serviceName} Account`}
									</h2>
								</Col>
							</Row>

							<Row>
								<Col mdOffset={3} md={6}>
									<h4 style={stringStyle}>
										{this.props.language === 'fi'
											? 'Ensimmäisen natiivikampanjasi aloittaminen käy käden käänteessä'
											: 'Launching your first campaign is easy!'}
									</h4>
								</Col>
							</Row>
						</>
					)}
					<Row>
						<Col mdOffset={3} md={6} smOffset={2} sm={8}>
							{this.state.flash ? displayFlash() : ''}
						</Col>
					</Row>

					{!this.props.embed ? (
						<SignupForm
							{...this.state}
							setAccountData={this.setAccountData}
							createAccount={this.createAccount}
							language={this.props.language}
							embed={this.props.embed}
							serviceName={this.serviceName}
						/>
					) : (
						<SignupFormNew
							{...this.state}
							setAccountData={this.setAccountData}
							createAccount={this.createAccount}
							language={this.props.language}
							embed={this.props.embed}
							serviceName={this.serviceName}
						/>
					)}
				</Grid>
			</div>
		)
	}
}
export default Signup
